import { useEffect, useRef, useState } from 'react';
import { useTheme } from '../../theme/ThemeContext';

const LEAFLET_CSS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
const LEAFLET_JS  = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
const GOLD        = "#d39a21";
const VENDOR_GREEN = "#4a9e6b";
const NU          = "var(--font-body)";

let leafletPromise = null;
function loadLeaflet() {
  if (leafletPromise) return leafletPromise;
  leafletPromise = new Promise((resolve) => {
    if (window.L) { resolve(window.L); return; }
    if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet"; link.href = LEAFLET_CSS;
      document.head.appendChild(link);
    }
    const script = document.createElement("script");
    script.src = LEAFLET_JS;
    script.onload = () => resolve(window.L);
    document.head.appendChild(script);
  });
  return leafletPromise;
}

// Curated country centroids. A circle sits at the country's centre rather than at
// the middle of wherever its listings happen to cluster, which reads better when a
// country has one or two pins in one corner of it.
//
// This table is NOT the gate on whether a country appears, see coordsFor(). It
// used to be, and 28 of the 44 countries with published listings (Scotland, Turkey,
// Denmark, Qatar...) were silently absent from the map because nobody had added a
// row here. A country going live must never depend on someone remembering this file.
const COUNTRY_COORDS = {
  italy:          [41.8719, 12.5674],
  france:         [46.2276, 2.2137],
  england:        [52.5, -1.5],
  spain:          [40.4637, -3.7492],
  greece:         [39.0742, 21.8243],
  portugal:       [39.3999, -8.2245],
  switzerland:    [46.8182, 8.2275],
  germany:        [51.1657, 10.4515],
  ireland:        [53.4129, -8.2439],
  usa:            [39.5, -98.35],
  canada:         [56.1304, -106.3468],
  uae:            [23.4241, 53.8478],
  morocco:        [31.7917, -7.0926],
  'south-africa': [-30.5595, 22.9375],
  sweden:         [60.1282, 18.6435],
  hungary:        [47.1625, 19.5033],
  romania:        [45.9432, 24.9668],
  indonesia:      [-0.7893, 113.9213],
  thailand:       [15.8700, 100.9925],
  bali:           [-8.6506, 115.2625],
  // Countries carrying published listings that the map used to drop on the floor.
  scotland:       [56.4907, -4.2026],
  wales:          [52.1307, -3.7837],
  'northern-ireland': [54.7877, -6.4923],
  denmark:        [56.2639, 9.5018],
  austria:        [47.5162, 14.5501],
  poland:         [51.9194, 19.1451],
  'czech-republic': [49.8175, 15.4730],
  slovenia:       [46.1512, 14.9955],
  croatia:        [45.1000, 15.2000],
  montenegro:     [42.7087, 19.3744],
  turkey:         [38.9637, 35.2433],
  cyprus:         [35.1264, 33.4299],
  gibraltar:      [36.1408, -5.3536],
  qatar:          [25.3548, 51.1839],
  'saudi-arabia': [23.8859, 45.0792],
  'united-arab-emirates': [23.4241, 53.8478], // listings use the full slug; `uae` above is the alias
  maldives:       [3.2028, 73.2207],
  malaysia:       [4.2105, 101.9758],
  cambodia:       [12.5657, 104.9910],
  tanzania:       [-6.3690, 34.8888],
  mexico:         [23.6345, -102.5528],
  'costa-rica':   [9.7489, -83.7534],
  belize:         [17.1899, -88.4976],
  jamaica:        [18.1096, -77.2975],
  bahamas:        [25.0343, -77.3963],
  'st-lucia':     [13.9094, -60.9789],
  'st-vincent-and-the-grenadines': [12.9843, -61.2872],
  'british-virgin-islands': [18.4207, -64.6400],
};

const COUNTRY_ZOOM = {
  england: 7, ireland: 7, portugal: 7, switzerland: 7, greece: 7,
  usa: 4, canada: 4, indonesia: 5, 'south-africa': 5,
  scotland: 6, wales: 7, 'northern-ireland': 8, denmark: 7, austria: 7,
  poland: 6, 'czech-republic': 7, slovenia: 8, croatia: 7, montenegro: 8,
  turkey: 5, cyprus: 8, gibraltar: 11, qatar: 8, 'saudi-arabia': 5,
  'united-arab-emirates': 7, maldives: 6, malaysia: 6, cambodia: 7,
  tanzania: 5, mexico: 5, 'costa-rica': 7, belize: 8, jamaica: 8,
  bahamas: 7, 'st-lucia': 10, 'st-vincent-and-the-grenadines': 9,
  'british-virgin-islands': 10,
};

// Where to draw a country's circle: the curated centroid when we have one, else the
// mean of its own listing pins. The fallback is what makes a newly-launched country
// appear the day its first listing goes live, with no code change. Only a country
// with no centroid AND no geocoded listing has nowhere to go.
function coordsFor(slug, listings) {
  const curated = COUNTRY_COORDS[slug];
  if (curated) return curated;
  const pins = (listings || []).filter((l) => l.countrySlug === slug && l.lat && l.lng);
  if (!pins.length) return null;
  return [
    pins.reduce((s, l) => s + Number(l.lat), 0) / pins.length,
    pins.reduce((s, l) => s + Number(l.lng), 0) / pins.length,
  ];
}

// ── Popup HTML for a listing pin ─────────────────────────────────────────────
function listingPopupHtml(listing, accentColor) {
  // Try .src first (buildCardImgs format), fallback .url, fallback thumbnail
  const imgObj = Array.isArray(listing.imgs) ? listing.imgs[0] : null;
  const img    = listing.thumbnail
    || (typeof imgObj === "string" ? imgObj : (imgObj?.src || imgObj?.url || ""))
    || "";
  const name     = listing.name || "Listing";
  const cat      = listing.categorySlug || listing.category || "";
  const catLabel = cat.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) || "Listing";
  const loc      = [listing.city, listing.region].filter(Boolean).join(", ");

  return `
    <div style="
      font-family:${NU};
      width:200px;
      background:#0f0f0f;
      border:1px solid ${accentColor}55;
      border-radius:4px;
      overflow:hidden;
      cursor:pointer;
    ">
      ${img ? `<img src="${img}" alt="${name}" style="width:100%;height:100px;object-fit:cover;display:block;" />` : ""}
      <div style="padding:12px 14px;">
        <div style="font-size:13px;font-weight:600;color:#f5f0e8;margin-bottom:3px;line-height:1.3">${name}</div>
        ${loc ? `<div style="font-size:10px;color:rgba(255,255,255,0.38);margin-bottom:5px">${loc}</div>` : ""}
        <div style="font-size:9px;letter-spacing:0.15em;text-transform:uppercase;color:${accentColor};opacity:0.75;margin-bottom:10px">${catLabel}</div>
        <div style="font-size:10px;color:${accentColor};letter-spacing:0.08em">View listing →</div>
      </div>
    </div>
  `;
}

// ── Small map control button ──────────────────────────────────────────────────
function CtrlBtn({ label, title, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      title={title}
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: 32, height: 32,
        background: hov ? "rgba(211,154,33,0.15)" : "rgba(15,13,10,0.88)",
        border: `1px solid ${hov ? GOLD : "rgba(255,255,255,0.15)"}`,
        borderRadius: 4,
        color: hov ? GOLD : "rgba(255,255,255,0.7)",
        fontFamily: NU,
        fontSize: 16,
        lineHeight: 1,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "all 0.2s",
        backdropFilter: "blur(6px)",
        userSelect: "none",
      }}
    >
      {label}
    </button>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
export default function GlobalHeatMap({ destinations, listings, focusedCountry, focusedRegion, onCountryFocus, onListingClick }) {
  const C = useTheme();
  const mapContainer    = useRef(null);
  const map             = useRef(null);
  const countryMarkersRef = useRef([]);
  const listingMarkersRef = useRef([]);

  // No local country state any more. Focus is owned by the page and shared with the
  // list, so clicking a circle here and focusing a row there can never disagree.

  // Track the last country we flew to, don't re-fly the same country twice
  const lastFlownCountry  = useRef(null);
  // Once the user manually drags or zooms, stop all auto-flying
  const userHasInteracted = useRef(false);

  // Keep latest callbacks and data in refs so event handlers never go stale
  const onListingClickRef = useRef(onListingClick);
  useEffect(() => { onListingClickRef.current = onListingClick; }, [onListingClick]);
  const listingsRef = useRef(listings);
  useEffect(() => { listingsRef.current = listings; }, [listings]);

  // One source of truth for "which country is showing pins", set either by focusing a
  // row in the list or by clicking a circle on the map, and cleared only by reset.
  const effectiveCountry = focusedCountry;

  // ── Reset to world view ───────────────────────────────────────────────────
  function resetView() {
    if (!map.current) return;
    map.current.flyTo([46, 12], 4, { animate: true, duration: 0.8 });
    onCountryFocus?.(null);
    lastFlownCountry.current = null;
    // Don't reset userHasInteracted, reset is itself a user action,
    // but allow the single reset fly by directly calling flyTo above.
  }

  // ── Init map once ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapContainer.current) return;

    loadLeaflet().then((L) => {
      if (map.current) return;

      // Popup styles
      if (!document.getElementById("lwd-map-popup-style")) {
        const style = document.createElement("style");
        style.id = "lwd-map-popup-style";
        style.textContent = `
          .lwd-listing-popup .leaflet-popup-content-wrapper {
            background: transparent !important;
            border: none !important;
            box-shadow: 0 8px 32px rgba(0,0,0,0.6) !important;
            padding: 0 !important;
            border-radius: 4px !important;
          }
          .lwd-listing-popup .leaflet-popup-content { margin: 0 !important; }
          .lwd-listing-popup .leaflet-popup-tip-container { display: none !important; }
          .lwd-listing-popup .leaflet-popup-close-button { display: none !important; }
        `;
        document.head.appendChild(style);
      }

      // fadeAnimation: false — Leaflet fades new tiles in via requestAnimationFrame, and
      // this map mounts below the fold, where the browser throttles rAF. The fade never
      // completed and every tile sat at opacity 0, so the panel rendered as flat grey
      // with the basemap invisible. Tiles now paint at full opacity immediately.
      map.current = L.map(mapContainer.current, {
        zoomControl: false,
        fadeAnimation: false,
      }).setView([46, 12], 4);

      // CARTO Voyager: a light, conventional basemap — land, water and labels read
      // the way people expect a map to look. Replaces dark_all, which made the
      // destination overview hard to orient in. Gold markers still carry the brand.
      L.tileLayer(`https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=${import.meta.env.VITE_CARTO_API_KEY}`, {
        attribution: "&copy; CartoDB",
        maxZoom: 19,
        opacity: 1,
      }).addTo(map.current);

      // Only dragstart counts as "user took manual control", zoomstart fires
      // during our own programmatic flyToBounds and must not block hover-fly.
      map.current.on("dragstart", () => { userHasInteracted.current = true; });

      // Leaflet caches the container size at init. This map mounts inside a grid
      // column that has not been laid out yet, so Leaflet measured a stale box and
      // the tile fade-in never completed — tiles stayed stuck at opacity 0.08, which
      // read as a flat grey panel with no basemap at all. Same fix as MASTERMap.
      requestAnimationFrame(() => map.current?.invalidateSize());
    });

    // The container can change size repeatedly — grid reflow, the mobile/desktop
    // switch, the browser window. A one-shot invalidateSize would fix only the first
    // of those, so keep observing for as long as the map is mounted.
    let ro = null;
    if (mapContainer.current && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => map.current?.invalidateSize());
      ro.observe(mapContainer.current);
    }

    return () => {
      if (ro) ro.disconnect();
      if (map.current) { map.current.remove(); map.current = null; }
    };
  }, []);

  // ── Country overview circles ──────────────────────────────────────────────
  useEffect(() => {
    if (!destinations || destinations.length === 0) return;

    loadLeaflet().then((L) => {
      if (!map.current) return;

      countryMarkersRef.current.forEach((m) => map.current.removeLayer(m));
      countryMarkersRef.current = [];

      const maxCount = Math.max(...destinations.map((d) => d.venueCount + d.vendorCount));

      destinations.forEach((dest) => {
        const coords = coordsFor(dest.slug, listingsRef.current);
        if (!coords) return;

        const intensity = (dest.venueCount + dest.vendorCount) / maxCount;
        const color  = intensity > 0.7 ? GOLD : intensity > 0.4 ? "#c89520" : "#b8911f";
        const radius = 8 + intensity * 12;

        const marker = L.circleMarker(coords, {
          radius,
          fillColor: color,
          color: "#f5f0e8",
          weight: 1,
          opacity: 0.9,
          fillOpacity: 0.8,
          bubblingMouseEvents: false,
        })
          .bindTooltip(
            `<div style="font-family:sans-serif;color:#1a1714;font-size:12px;padding:4px 8px;">
              <strong>${dest.name}</strong> &nbsp;
              <span style="opacity:0.6">${dest.venueCount + dest.vendorCount} listings</span>
            </div>`,
            { sticky: true, className: "" }
          );

        // Click: open the cluster, fitBounds to all pins in this country
        marker.on("click", () => {
          if (!map.current) return;
          const pins = (listingsRef.current || []).filter(
            (l) => l.countrySlug === dest.slug && l.lat && l.lng
          );
          if (pins.length > 0) {
            // Fit to the actual spread of listing pins, this is the "open cluster" effect
            const bounds = L.latLngBounds(pins.map((l) => [l.lat, l.lng]));
            map.current.flyToBounds(bounds, {
              padding:  [60, 60],
              maxZoom:  10,
              animate:  true,
              duration: 0.7,
            });
          } else {
            // No pins with coords yet, fall back to country centre
            const zoom = COUNTRY_ZOOM[dest.slug] || 6;
            map.current.flyTo(coords, zoom, { animate: true, duration: 0.7 });
          }
          lastFlownCountry.current = dest.slug;
          userHasInteracted.current = false;
          onCountryFocus?.(dest.slug);
        });

        // Cursor hint
        marker.on("mouseover", function () { this.getElement()?.style && (this.getElement().style.cursor = "pointer"); });

        marker.addTo(map.current);
        countryMarkersRef.current.push(marker);
      });
    });
    // `listings` is a dependency because coordsFor() falls back to listing pins for a
    // country with no curated centroid: without it, a country whose listings land
    // after this effect would stay off the map until something else forced a redraw.
  }, [destinations, listings]);

  // ── Listing pins, driven by effectiveCountry (+ optional region) ──────────
  useEffect(() => {
    loadLeaflet().then((L) => {
      if (!map.current) return;

      // Clear old pins
      listingMarkersRef.current.forEach((m) => map.current.removeLayer(m));
      listingMarkersRef.current = [];

      if (effectiveCountry) {
        // Dim country circles so pins stand out
        countryMarkersRef.current.forEach((m) => m.setStyle({ fillOpacity: 0.1, opacity: 0.1 }));

        const countryListings = (listings || []).filter(
          (l) => l.countrySlug === effectiveCountry && l.lat && l.lng
        );
        // A focused region narrows the camera, not the pin set: the surrounding
        // country pins stay drawn (dimmed) so the region reads in context rather
        // than as an isolated cluster floating on an empty basemap.
        const regionListings = focusedRegion
          ? countryListings.filter((l) => l.regionSlug === focusedRegion)
          : countryListings;
        const flyTargets = regionListings.length > 0 ? regionListings : countryListings;

        // Fly / fit to pins if we haven't already and user hasn't taken manual control.
        // Keyed on country+region so moving between regions re-frames each time.
        const flyKey = `${effectiveCountry}|${focusedRegion || ""}`;
        if (!userHasInteracted.current && lastFlownCountry.current !== flyKey) {
          if (flyTargets.length > 0) {
            const bounds = L.latLngBounds(flyTargets.map((l) => [l.lat, l.lng]));
            map.current.flyToBounds(bounds, {
              padding: [60, 60],
              // A region gets to zoom closer than a country; a single-listing region
              // would otherwise slam to max zoom on one pin.
              maxZoom: focusedRegion ? (flyTargets.length === 1 ? 11 : 12) : 10,
              animate: true,
              duration: 0.7,
            });
          } else {
            const coords = COUNTRY_COORDS[effectiveCountry];
            const zoom   = COUNTRY_ZOOM[effectiveCountry] || 6;
            if (coords) map.current.flyTo(coords, zoom, { animate: true, duration: 0.7 });
          }
          lastFlownCountry.current = flyKey;
        }

        countryListings.forEach((listing) => {
          const isVenue     = listing.type === "venue" || listing.category === "wedding-venues" || !listing.category;
          const accentColor = isVenue ? GOLD : VENDOR_GREEN;
          const inRegion    = !focusedRegion || listing.regionSlug === focusedRegion;

          const marker = L.circleMarker([listing.lat, listing.lng], {
            radius: inRegion ? 9 : 6,
            fillColor: accentColor,
            color: "#ffffff",
            weight: 2,
            opacity: inRegion ? 1 : 0.35,
            fillOpacity: inRegion ? 0.95 : 0.3,
            bubblingMouseEvents: false,
          });

          marker.bindPopup(listingPopupHtml(listing, accentColor), {
            className: "lwd-listing-popup",
            maxWidth: 220,
            autoPan: true,
          });

          // Click → navigate to listing
          marker.on("click", () => {
            onListingClickRef.current?.(listing);
          });

          // Pulse on hover, open popup
          marker.on("mouseover", function () {
            // Hovering an out-of-region pin brings it fully back — it is still a real
            // listing you can click, so it must not stay ghosted under the cursor.
            this.setStyle({ radius: 12, weight: 2.5, opacity: 1, fillOpacity: 0.95 });
            this.openPopup();
          });
          marker.on("mouseout", function () {
            this.setStyle({
              radius: inRegion ? 9 : 6,
              weight: 2,
              opacity: inRegion ? 1 : 0.35,
              fillOpacity: inRegion ? 0.95 : 0.3,
            });
          });

          marker.addTo(map.current);
          listingMarkersRef.current.push(marker);
        });

      } else {
        // Nothing hovered or clicked, restore country circles, map stays put
        countryMarkersRef.current.forEach((m) => m.setStyle({ fillOpacity: 0.8, opacity: 0.9 }));
      }
    });
  }, [effectiveCountry, focusedRegion, listings]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    // Backdrop matches Voyager's land tone, so no dark frame shows around the
    // tiles or flashes while they load.
    <div style={{ position: "relative", width: "100%", height: "100%", background: "#f4f1ec" }}>
      {/* Map canvas */}
      <div ref={mapContainer} style={{ width: "100%", height: "100%" }} />

      {/* Map controls, top right */}
      <div style={{
        position: "absolute",
        top: 16,
        right: 16,
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}>
        <CtrlBtn label="+" title="Zoom in"  onClick={() => map.current?.zoomIn()} />
        <CtrlBtn label="−" title="Zoom out" onClick={() => map.current?.zoomOut()} />
        <CtrlBtn label="↺" title="Reset view" onClick={resetView} />
      </div>

      {/* Active country chip, tells user which country is pinned */}
      {effectiveCountry && (
        <div style={{
          position:    "absolute",
          top:         16,
          left:        16,
          zIndex:      1000,
          display:     "flex",
          alignItems:  "center",
          gap:         8,
          background:  "rgba(15,13,10,0.88)",
          border:      `1px solid ${GOLD}55`,
          borderRadius: 4,
          padding:     "6px 12px",
          backdropFilter: "blur(6px)",
        }}>
          <span style={{ fontFamily: NU, fontSize: 11, color: GOLD, letterSpacing: "0.1em", textTransform: "uppercase" }}>
            {(focusedRegion || effectiveCountry).replace(/-/g, " ")}
          </span>
          <button
            onClick={() => setActiveCountry(null)}
            style={{
              background: "none", border: "none",
              color: "rgba(255,255,255,0.4)",
              cursor: "pointer", fontSize: 12, padding: 0, lineHeight: 1,
            }}
            title="Clear"
          >✕</button>
        </div>
      )}
    </div>
  );
}
